/**
 * ブランドスコープの純粋ロジック
 *
 * ヘッダのブランドスイッチャーが選んだブランド ID ('' = 全ブランド) に対して、brandId 付き資産の
 * 表示可否やブランド名の解決を行う。会社共通 (brandId なし) の資産は常に表示される。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business ブランドスイッチャーの絞り込み判定・ブランド名解決・選択の永続化キーを提供する純粋ロジック。
 * @graph-connects none
 */
import type { CreativeBrand } from '@cortex/domain-creative/web';

/** ブランドスコープ選択の localStorage キー。 @graph-connects none */
export const BRAND_SCOPE_STORAGE_KEY = 'creative-brand-scope';

/** 全ブランドを表すスコープ値。 @graph-connects none */
export const ALL_BRANDS_SCOPE = '';

/**
 * 資産がスコープ内かを判定する。
 *
 * 会社共通 (brandId なし) は常に true。全ブランドスコープも常に true。
 * @param brandId 資産の帰属ブランド (undefined = 会社共通)
 * @param scope 選択中のブランド ID ('' = 全ブランド)
 * @returns スコープ内なら true
 * @graph-connects none
 */
export function matchesBrandScope(brandId: string | undefined, scope: string): boolean {
  return scope === ALL_BRANDS_SCOPE || brandId === undefined || brandId === scope;
}

/**
 * ブランド ID から表示名を解決する。マスタ未登録 ID はそのまま返す。
 * @param brands ブランドマスタ
 * @param brandId ブランド ID
 * @returns ブランドの表示名
 * @graph-connects none
 */
export function brandNameOf(brands: CreativeBrand[], brandId: string): string {
  return brands.find((brand) => brand.id === brandId)?.name ?? brandId;
}

/**
 * localStorage の保存値を有効なスコープに解決する。
 *
 * マスタに存在しない ID (削除済みブランド等) は全ブランドにフォールバックする。
 * @param stored 保存値 (null = 未保存)
 * @param brands ブランドマスタ
 * @returns 有効なスコープ値
 * @graph-connects none
 */
export function resolveStoredBrandScope(stored: string | null, brands: CreativeBrand[]): string {
  if (stored === null || stored === ALL_BRANDS_SCOPE) {
    return ALL_BRANDS_SCOPE;
  }
  return brands.some((brand) => brand.id === stored) ? stored : ALL_BRANDS_SCOPE;
}
